import axios from 'axios';
import { useEffect, useState } from 'react';

export interface TryResult {
  try_number: number;
  wpm: number;
  raw_wpm: number;
  accuracy: number;
  adjusted_wpm: number;
  consistency: number;
  errors_per_minute: number;
  total_words: number;
  correct_words: number;
  incorrect_words: number;
  test_duration: number;
  typed_text: string;
  original_text: string;
  test_taken_datetime?: string;
}

/** Where the candidate's code stands (from the server) */
export interface CodeState {
  code: string;
  max_tries: number;
  tries: TryResult[];
  best_try: number | null;
  next_try: number | null;
  open: boolean;
  /** Has tries and isn't final yet */
  in_progress: boolean;
  finished: boolean;
  expires_at: string | null;
}

/** A just-finished try as kept in sessionStorage; _submitted is false when the server never confirmed it */
export type LocalTry = TryResult & { _submitted?: boolean };

export const MAX_TRIES = 3;

/** Milliseconds until the code's hour runs out (null when there's no deadline), ticking every second */
export function useTimeLeft(expiresAt: string | null | undefined) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);
  return expiresAt ? Math.max(0, new Date(expiresAt).getTime() - now) : null;
}

export const formatTimeLeft = (ms: number) => {
  const minutes = Math.ceil(ms / 60000);
  if (ms < 60000) return 'less than a minute';
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
};

export const authHeaders = () => {
  const token = sessionStorage.getItem('userToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchCodeState = async (): Promise<CodeState> => {
  const response = await axios.get('/api/test/status', { headers: authHeaders(), timeout: 10000 });
  return response.data.state;
};

export const finishCode = async (): Promise<CodeState> => {
  const response = await axios.post('/api/test/finish', {}, { headers: authHeaders(), timeout: 10000 });
  return response.data.state;
};

export const draftKey = (code: string) => `testDraft_${code}`;
